import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MANDANT_TYPE_LABELS, MandantTypeSchema, type MandantType } from '../../lib/api-types';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { Dialog } from '../../components/ui/Dialog';
import { Input } from '../../components/ui/Input';
import { Pagination } from '../../components/ui/Pagination';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import { Table, TBody, TD, TH, THead, TR } from '../../components/ui/Table';
import { useCreateMandant, useMandants } from './hooks';
import { MandantForm } from './MandantForm';

const PAGE_SIZE = 10;

export function MandantsListPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [type, setType] = useState<MandantType | ''>('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const query = useMandants({ page, pageSize: PAGE_SIZE, search, type, includeInactive });
  const createMandant = useCreateMandant();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">Mandants</h1>
        <Button onClick={() => setCreateOpen(true)}>Nouveau mandant</Button>
      </div>

      <Card>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-48 flex-1">
              <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="search">
                Rechercher
              </label>
              <Input
                id="search"
                placeholder="Nom du mandant…"
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
            </div>
            <div className="w-48">
              <label
                className="mb-1 block text-sm font-medium text-slate-700"
                htmlFor="type-filter"
              >
                Type
              </label>
              <Select
                id="type-filter"
                value={type}
                onChange={(e) => {
                  setPage(1);
                  setType(e.target.value as MandantType | '');
                }}
              >
                <option value="">Tous les types</option>
                {MandantTypeSchema.options.map((t) => (
                  <option key={t} value={t}>
                    {MANDANT_TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
            </div>
            <label className="flex items-center gap-2 pb-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => {
                  setPage(1);
                  setIncludeInactive(e.target.checked);
                }}
              />
              Inclure les inactifs
            </label>
          </div>

          {query.isError ? (
            <Alert variant="error">{toDisplayError(query.error).message}</Alert>
          ) : null}

          {query.isLoading ? (
            <div className="py-8 text-center">
              <Spinner label="Chargement des mandants…" />
            </div>
          ) : query.data && query.data.items.length > 0 ? (
            <>
              <Table>
                <THead>
                  <TR>
                    <TH>Nom</TH>
                    <TH>Type</TH>
                    <TH>État</TH>
                    <TH className="text-right">Action</TH>
                  </TR>
                </THead>
                <TBody>
                  {query.data.items.map((m) => (
                    <TR key={m.id}>
                      <TD className="font-medium text-slate-900">{m.nom}</TD>
                      <TD>{MANDANT_TYPE_LABELS[m.type]}</TD>
                      <TD>
                        {m.isActive ? (
                          <Badge tone="success">Actif</Badge>
                        ) : (
                          <Badge tone="neutral">Inactif</Badge>
                        )}
                      </TD>
                      <TD className="text-right">
                        <Link
                          to={`/mandants/${m.id}`}
                          className="text-sm font-medium text-brand-600 hover:underline"
                        >
                          Détails
                        </Link>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
              <Pagination
                page={query.data.page}
                totalPages={query.data.totalPages}
                total={query.data.total}
                onPageChange={setPage}
              />
            </>
          ) : (
            <p className="py-8 text-center text-sm text-slate-500">Aucun mandant trouvé.</p>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} title="Nouveau mandant">
        {createMandant.isError ? (
          <Alert
            variant="error"
            className="mb-4"
            details={toDisplayError(createMandant.error).details}
          >
            {toDisplayError(createMandant.error).message}
          </Alert>
        ) : null}
        <MandantForm
          submitLabel="Créer"
          pending={createMandant.isPending}
          onCancel={() => setCreateOpen(false)}
          onSubmit={(input) =>
            createMandant.mutate(input, {
              onSuccess: () => setCreateOpen(false),
            })
          }
        />
      </Dialog>
    </div>
  );
}
